import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../services/api';
import './UserProfileModal.css';

interface UserProfileModalProps {
    isOpen: boolean;
    onClose: () => void;
    isForcedPhone?: boolean;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose, isForcedPhone = false }) => {
    const { user, updateUser } = useAuth();

    const [fullName, setFullName] = useState('');
    const [expertiseClass, setExpertiseClass] = useState('A Sınıfı İSG Uzmanı');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    
    // Mobil PIN
    const [mobilePin, setMobilePin] = useState('');
    
    // Şifre Değiştirme
    const [wantChangePassword, setWantChangePassword] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (user) {
            setFullName(user.fullName || '');
            setExpertiseClass((user as any).expertiseClass || 'A Sınıfı İSG Uzmanı');
            setEmail(user.email || '');
            setPhone(user.phone || '');
            setMobilePin('');
            setWantChangePassword(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            setError('');
            setSuccess('');
        }
    }, [user, isOpen]);

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!fullName.trim()) {
            setError('Lütfen Ad Soyad alanını doldurunuz.');
            return;
        }

        // Telefon Kontrolü
        const cleanDigits = phone.replace(/\D/g, '');
        let normPhone = cleanDigits;
        if (cleanDigits.startsWith('90') && cleanDigits.length === 12) {
            normPhone = cleanDigits.slice(2);
        } else if (cleanDigits.startsWith('0') && cleanDigits.length === 11) {
            normPhone = cleanDigits.slice(1);
        }

        if (isForcedPhone || phone.trim()) {
            if (normPhone.length !== 10 || !normPhone.startsWith('5')) {
                setError('Lütfen geçerli bir cep telefonu numarası giriniz (Örn: 05xx xxx xx xx).');
                return;
            }
        }

        // Mobil PIN Kontrolü (varsa)
        if (mobilePin.trim()) {
            const pinStr = mobilePin.trim();
            if (pinStr.length !== 4 || !/^\d{4}$/.test(pinStr)) {
                setError('Mobil PIN tam olarak 4 rakamdan oluşmalıdır.');
                return;
            }
            if (pinStr === '1234' || pinStr === '4321') {
                setError('Mobil PIN ardışık (1234, 4321) olamaz.');
                return;
            }
            if (/^(\d)\1{3}$/.test(pinStr)) {
                setError('Mobil PIN aynı rakamlardan (1111 vb.) oluşamaz.');
                return;
            }
            const y = parseInt(pinStr, 10);
            if (y >= 1900 && y <= 2100) {
                setError('Mobil PIN bir yıl (doğum yılı vb.) olamaz.');
                return;
            }
        }

        // Şifre Değiştirme Kontrolü (varsa)
        if (wantChangePassword) {
            if (!currentPassword) {
                setError('Şifrenizi değiştirmek için mevcut web şifrenizi girmelisiniz.');
                return;
            }
            if (newPassword.length < 8) {
                setError('Yeni şifreniz en az 8 karakter olmalıdır.');
                return;
            }
            if (newPassword !== confirmNewPassword) {
                setError('Yeni şifreler birbiriyle uyuşmuyor.');
                return;
            }
        }

        try {
            setLoading(true);
            const payload: any = {
                fullName: fullName.trim(),
                expertiseClass: expertiseClass,
                phone: normPhone
            };

            if (mobilePin.trim()) {
                payload.mobilePin = mobilePin.trim();
            }

            if (wantChangePassword) {
                payload.currentPassword = currentPassword;
                payload.newPassword = newPassword;
            }

            const res = await authApi.updateProfile(payload);
            if (res.success && res.user) {
                updateUser(res.user);
                setSuccess('Kişisel bilgileriniz başarıyla güncellendi! ✅');
                setTimeout(() => {
                    onClose();
                }, 1200);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Güncelleme sırasında bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="user-profile-modal-overlay" onClick={isForcedPhone ? undefined : onClose}>
            <div className="user-profile-modal-card" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="user-profile-modal-header">
                    <h3 className="user-profile-modal-title">
                        <span>👤</span> Kişisel Bilgilerim ve Güvenlik
                    </h3>
                    {!isForcedPhone && (
                        <button
                            type="button"
                            className="user-profile-modal-close"
                            onClick={onClose}
                            title="Kapat"
                        >
                            &times;
                        </button>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="user-profile-modal-form">
                    {/* Body */}
                    <div className="user-profile-modal-body">
                        {isForcedPhone && (
                            <div className="upm-alert-box">
                                ⚠️ Mobil PWA saha girişi ve hesap güvenliğiniz için lütfen cep telefonu numaranızı kaydediniz.
                            </div>
                        )}

                        {error && <div className="upm-error-alert">{error}</div>}
                        {success && <div className="upm-success-alert">{success}</div>}

                        {/* Profil Alanları: İki Sütunlu Kompakt Satır */}
                        <div className="upm-section">
                            <div className="upm-section-header">
                                <h4 className="upm-section-title">Hesap Bilgileri</h4>
                            </div>

                            <div className="upm-grid-2">
                                <div className="upm-form-group">
                                    <label className="upm-label">Ad Soyad *</label>
                                    <input
                                        type="text"
                                        className="upm-input"
                                        value={fullName}
                                        onChange={(e) => setFullName(e.target.value)}
                                        required
                                        placeholder="Adınız Soyadınız"
                                    />
                                </div>

                                <div className="upm-form-group">
                                    <label className="upm-label">Uzmanlık Sınıfı / Unvan *</label>
                                    <select
                                        className="upm-input"
                                        value={expertiseClass}
                                        onChange={(e) => setExpertiseClass(e.target.value)}
                                    >
                                        <option value="A Sınıfı İSG Uzmanı">A Sınıfı İSG Uzmanı</option>
                                        <option value="B Sınıfı İSG Uzmanı">B Sınıfı İSG Uzmanı</option>
                                        <option value="C Sınıfı İSG Uzmanı">C Sınıfı İSG Uzmanı</option>
                                        <option value="İşyeri Hekimi">İşyeri Hekimi</option>
                                        <option value="Diğer Sağlık Personeli">Diğer Sağlık Personeli</option>
                                    </select>
                                </div>
                            </div>

                            <div className="upm-grid-2">
                                <div className="upm-form-group">
                                    <label className="upm-label">
                                        <span>Cep Telefonu *</span>
                                        <span className="upm-hint-tag">Mobil Giriş</span>
                                    </label>
                                    <input
                                        type="tel"
                                        className="upm-input"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        required={isForcedPhone}
                                        placeholder="05xx xxx xx xx"
                                    />
                                </div>

                                <div className="upm-form-group">
                                    <label className="upm-label">
                                        <span>E-Posta Adresi</span>
                                        <span className="upm-label-badge">🛡️ Doğrulanmış</span>
                                    </label>
                                    <input
                                        type="email"
                                        className="upm-input"
                                        value={email}
                                        disabled
                                        title="E-posta adresi güvenlik nedeniyle değiştirilemez"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Mobil PIN Alanı */}
                        <div className="upm-section">
                            <div className="upm-section-header">
                                <h4 className="upm-section-title">Mobil Hızlı Giriş (4 Haneli PIN)</h4>
                                <div className={`upm-pin-status ${user?.hasMobilePin ? 'active' : 'inactive'}`}>
                                    {user?.hasMobilePin ? '🟢 PIN Aktif' : '⚪ Belirlenmedi'}
                                </div>
                            </div>

                            <div className="upm-form-group">
                                <div className="upm-pin-inline">
                                    <input
                                        type="password"
                                        className="upm-input upm-pin-input"
                                        value={mobilePin}
                                        onChange={(e) => {
                                            const v = e.target.value.replace(/\D/g, '');
                                            if (v.length <= 4) setMobilePin(v);
                                        }}
                                        maxLength={4}
                                        inputMode="numeric"
                                        placeholder="••••"
                                    />
                                    <span className="upm-hint">
                                        {user?.hasMobilePin ? 'Değiştirmek için 4 rakam girin.' : 'Sahada 2 saniyede girmek için 4 rakam belirleyin.'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Web Şifre Değiştirme Sekmesi */}
                        <div className="upm-section">
                            <label className="upm-checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={wantChangePassword}
                                    onChange={(e) => setWantChangePassword(e.target.checked)}
                                />
                                <span>🔑 Web Giriş Şifremi Değiştir</span>
                            </label>

                            {wantChangePassword && (
                                <div className="upm-password-fields">
                                    <div className="upm-form-group">
                                        <label className="upm-label">Mevcut Şifre *</label>
                                        <input
                                            type="password"
                                            className="upm-input"
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            placeholder="Mevcut web şifreniz"
                                        />
                                    </div>

                                    <div className="upm-grid-2">
                                        <div className="upm-form-group">
                                            <label className="upm-label">Yeni Şifre (En az 8 Kr.) *</label>
                                            <input
                                                type="password"
                                                className="upm-input"
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="Yeni şifre"
                                            />
                                        </div>

                                        <div className="upm-form-group">
                                            <label className="upm-label">Yeni Şifre Tekrar *</label>
                                            <input
                                                type="password"
                                                className="upm-input"
                                                value={confirmNewPassword}
                                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                                placeholder="Şifre tekrarı"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="user-profile-modal-footer">
                        {!isForcedPhone && (
                            <button
                                type="button"
                                className="upm-btn upm-btn-cancel"
                                onClick={onClose}
                                disabled={loading}
                            >
                                Vazgeç
                            </button>
                        )}
                        <button
                            type="submit"
                            className="upm-btn upm-btn-primary"
                            disabled={loading}
                        >
                            {loading ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UserProfileModal;
